import { getContrast, getLuminance, parseToRgba, toHex } from 'color2k';

/**
 * Modify lightness of the color to match the target luminance.
 */
const setLuminance = (color: string, targetLum: number): string => {
  const [r, g, b] = parseToRgba(color);
  const currentLum = getLuminance(color);

  if (currentLum === 0) {
    // Non-proportional adjustment for colors with zero luminance
    const adjust = (c: number) => Math.min(255, Math.max(0, Math.round(c + targetLum * 255)));
    return toHex(`rgb(${adjust(r)}, ${adjust(g)}, ${adjust(b)})`);
  }

  const scale = Math.sqrt(targetLum / currentLum); // Proportional adjustment
  const adjust = (c: number) => Math.min(255, Math.max(0, Math.round(c * scale)));
  return toHex(`rgb(${adjust(r)}, ${adjust(g)}, ${adjust(b)})`);
};

/**
 * Adjust color2 to meet the target contrast ratio with color1.
 *
 * Opaque colors only.
 */
export const adjustLuminanceForContrast = (
  color1: string,
  color2: string,
  targetRatio: number = 4.5,
): string => {
  if (getContrast(color1, color2) >= targetRatio) return color2;

  if (parseToRgba(color1)[3] !== 1 || parseToRgba(color2)[3] !== 1) {
    throw new Error('adjustLuminanceForContrast: provided colors must be opaque');
  }

  const lum1 = getLuminance(color1);
  let targetLum;

  if (lum1 >= 0.5) {
    // The value 0.05 is subtracted from the target luminance to ensure a small adjustment
    // and avoid potential rounding errors when calculating the new luminance.
    targetLum = (lum1 + 0.05) / targetRatio - 0.05;
  } else {
    // If color1 is dark, lighten color2
    targetLum = (lum1 + 0.05) * targetRatio - 0.05;
  }

  // Ensure targetLum is within the valid range [0, 1]
  const clampedTargetLum = Math.max(0, Math.min(1, targetLum));

  return setLuminance(color2, clampedTargetLum);
};
